import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Key, User, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import getEnvironmentConfig from '../config/environment';

const Login = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  // 'email' = enter email; 'code' = enter 6-digit code; 'password' = admin/password
  const [step, setStep] = useState('email');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [expiresIn, setExpiresIn] = useState(null);
  // Password-login state (admin/jobotics2025 by default).
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { sendCode, verifyCode, login } = useAuth();
  const navigate = useNavigate();
  const config = getEnvironmentConfig();
  // Honor ?next=/flow etc. — server routes bounce here with the intended
  // destination; only same-origin paths are allowed (no open redirect).
  const afterLogin = () => {
    const next = new URLSearchParams(window.location.search).get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      window.location.href = next; // server-rendered surfaces (/flow) need a full load
    } else {
      navigate('/');
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    try {
      const ok = await login(username, password);
      if (ok) afterLogin();
    } catch (err) {
      console.error('Password login error:', err);
    }
    setLoading(false);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) {
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await sendCode(email);
      if (result.success) {
        setCodeSent(true);
        setStep('code');
        setExpiresIn(result.expiresInMinutes);
      }
    } catch (error) {
      console.error('Send code error:', error);
    }
    
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      return;
    }
    
    setLoading(true);
    
    try {
      const success = await verifyCode(email, code);
      if (success) {
        afterLogin();
      }
    } catch (error) {
      console.error('Verify code error:', error);
    }
    
    setLoading(false);
  };

  const handleResendCode = async () => {
    setCode('');
    setCodeSent(false);
    await handleSendCode({ preventDefault: () => {} });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto flex justify-center">
            <img 
              src={config.logoUrl} 
              alt="Company Logo" 
              className="h-48 w-auto"
              onError={(e) => {
                console.error('Logo failed to load:', config.logoUrl);
                e.target.style.display = 'none';
              }}
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {config.welcomeInfo}
          </h2>
        </div>
        
        {step === 'email' ? (
          <form className="mt-8 space-y-6" onSubmit={handleSendCode}>
            <div className="rounded-md shadow-sm">
              <div className="relative">
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-sylvan-500 focus:border-sylvan-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your email address"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !email}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-500 hover:bg-sylvan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending code...' : 'Send Verification Code'}
              </button>
            </div>

            {/* Divider + secondary admin/password path. The verification-code
                flow stays primary; the password path is a fallback for demos
                and shared accounts. */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300" /></div>
              <div className="relative flex justify-center text-xs uppercase tracking-wider"><span className="bg-canvas px-2 text-gray-500">or</span></div>
            </div>
            <button
              type="button"
              onClick={() => setStep('password')}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Lock className="h-4 w-4 text-gray-500" />
              Sign in with username & password
            </button>
          </form>
        ) : step === 'password' ? (
          <form className="mt-8 space-y-6" onSubmit={handlePasswordLogin}>
            <div className="rounded-md shadow-sm space-y-3">
              <div className="relative">
                <label htmlFor="username" className="sr-only">Username</label>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-sylvan-500 focus:border-sylvan-500 sm:text-sm"
                  placeholder="Username"
                />
              </div>
              <div className="relative">
                <label htmlFor="password" className="sr-only">Password</label>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full pl-10 pr-10 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-sylvan-500 focus:border-sylvan-500 sm:text-sm"
                  placeholder="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={loading || !username || !password}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-500 hover:bg-sylvan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('email'); setUsername(''); setPassword(''); }}
                className="w-full text-sm text-gray-600 hover:text-gray-900"
              >
                ← Use email verification instead
              </button>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleVerifyCode}>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-sm text-blue-800">
                {codeSent ? (
                  <>We've sent a 6-digit verification code to <strong>{email}</strong></>
                ) : (
                  'Enter the verification code sent to your email'
                )}
              </p>
              {expiresIn && (
                <p className="text-xs text-blue-600 mt-1">
                  Code expires in {expiresIn} minutes
                </p>
              )}
            </div>

            {/* Spam-box reminder — verification codes frequently get filtered
                into Junk / Spam by Outlook + Gmail because they're a new
                sender on a transactional domain. Highlighted so users
                actually notice it before clicking "Resend code". */}
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-md p-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong className="font-semibold">Don't see the code?</strong> Please check your
                {' '}<span className="font-semibold">Spam</span> or
                {' '}<span className="font-semibold">Junk</span> folder — it sometimes lands there.
              </div>
            </div>

            <div className="rounded-md shadow-sm">
              <div className="relative">
                <label htmlFor="code" className="sr-only">
                  Verification Code
                </label>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  required
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(value);
                  }}
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-sylvan-500 focus:border-sylvan-500 focus:z-10 sm:text-sm text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                />
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-500 hover:bg-sylvan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setCodeSent(false);
                }}
                className="w-full text-sm text-gray-600 hover:text-gray-900"
              >
                ← Back to email
              </button>

              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="w-full text-sm text-blue-600 hover:text-blue-500 disabled:opacity-50"
              >
                Resend code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login; 